(function(root, factory) {
    if (typeof module === 'object' && module.exports) {
        module.exports = { createInterpreterOfferAlert: factory };
    } else {
        root.InterpreterOfferAlert = factory(root);
    }
}(typeof window !== 'undefined' ? window : globalThis, function createInterpreterOfferAlert(environment) {
    'use strict';

    // Web-only preferences (AGENTS.md storage registry). The offer sound is
    // per device and ON unless this device explicitly turned it off
    // ('false'). Answer automatically is per account on this browser
    // ('vrs_interpreter_offer_auto_answer:<userId>') and OFF unless that
    // account turned it on ('true'): on a shared workstation one
    // interpreter's opt-in never answers for the next one.
    const CHIME_STORAGE_KEY = 'vrs_interpreter_offer_chime_enabled';
    const AUTO_ANSWER_STORAGE_KEY = 'vrs_interpreter_offer_auto_answer';
    // A repeating offer (the interpreter portal) sounds its two-tone this
    // often until the offer is answered, withdrawn or expires.
    const CHIME_REPEAT_MS = 2000;
    const FLASH_INTERVAL_MS = 900;
    const NOTIFICATION_TAG = 'malka-interpreter-offer';
    const CALL_OFFER_TITLE = 'Client waiting…';
    // Anything the interpreter does on the page. The browser lets sound start
    // from the same events (a touch counts from touchend/pointerup).
    const INTERACTION_EVENTS = [ 'pointerdown', 'pointerup', 'mousedown', 'keydown', 'touchend', 'click' ];

    let active = null;
    let audioContext = null;
    // The signed-in account Answer automatically belongs to: an id, or a
    // function the page gives that returns it. None: automatic answering is
    // off and cannot be turned on (every page but the dashboard).
    let autoAnswerOwner = null;
    let beacon = null;
    let chimeTimer = null;
    let cue = [];
    let interacted = false;
    let notification = null;
    let originalTitle = null;
    // Set by a page that sounds offers whenever they arrive (the dashboard):
    // any first click or key press there starts the audio.
    let soundOnInteraction = false;
    let titleTimer = null;
    const listeners = new Set();

    function getDocument() {
        return environment && environment.document;
    }

    function getNotificationApi() {
        return environment && environment.Notification;
    }

    function getStorage() {
        return environment && environment.localStorage;
    }

    function getAudioApi() {
        return environment && (environment.AudioContext || environment.webkitAudioContext);
    }

    function readPreference(key) {
        try {
            return getStorage()?.getItem(key) ?? null;
        } catch (error) {
            return null;
        }
    }

    function writePreference(key, enabled) {
        try {
            getStorage()?.setItem(key, enabled ? 'true' : 'false');
        } catch (error) {
            return false;
        }

        return true;
    }

    // Only an explicit 'false' turns the sound off. Anything else, including
    // a device that never chose (or storage that cannot be read), is on.
    function isChimeEnabled() {
        return readPreference(CHIME_STORAGE_KEY) !== 'false';
    }

    function autoAnswerStorageKey() {
        let owner = autoAnswerOwner;

        try {
            owner = typeof owner === 'function' ? owner() : owner;
        } catch (error) {
            owner = null;
        }
        owner = owner === null || owner === undefined ? '' : String(owner).trim();

        return owner ? AUTO_ANSWER_STORAGE_KEY + ':' + owner : null;
    }

    function isAutoAnswerEnabled() {
        const key = autoAnswerStorageKey();

        return Boolean(key) && readPreference(key) === 'true';
    }

    function hasUserInteraction() {
        if (interacted) {
            return true;
        }
        try {
            return environment?.navigator?.userActivation?.hasBeenActive === true;
        } catch (error) {
            return false;
        }
    }

    function emitChange() {
        const status = getStatus();

        listeners.forEach(listener => {
            try {
                listener(status);
            } catch (error) {
                // One page listener cannot silence the others.
            }
        });
    }

    function onChange(listener) {
        if (typeof listener !== 'function') {
            return () => undefined;
        }
        listeners.add(listener);

        return () => listeners.delete(listener);
    }

    function getNotificationPermission() {
        const NotificationApi = getNotificationApi();

        return NotificationApi ? String(NotificationApi.permission || 'default') : 'unsupported';
    }

    function requestNotificationPermission() {
        const NotificationApi = getNotificationApi();

        if (!NotificationApi || typeof NotificationApi.requestPermission !== 'function') {
            return Promise.resolve('unsupported');
        }
        if (NotificationApi.permission !== 'default') {
            return Promise.resolve(String(NotificationApi.permission));
        }

        let request;

        try {
            request = Promise.resolve(NotificationApi.requestPermission())
                .then(result => String(result || NotificationApi.permission || 'default'))
                .catch(() => String(NotificationApi.permission || 'default'));
        } catch (error) {
            request = Promise.resolve(String(NotificationApi.permission || 'default'));
        }

        return request.then(result => {
            emitChange();

            return result;
        });
    }

    // ── Sound ───────────────────────────────────────────────────────────
    // off: this device turned it off. unsupported: no Web Audio here.
    // blocked: the browser has not let this page start sound yet (it needs a
    // click or key press on the page). on: a cue would be heard now.
    function getAudioState() {
        if (!isChimeEnabled()) {
            return 'off';
        }
        if (!getAudioApi()) {
            return 'unsupported';
        }

        return audioContext && audioContext.state === 'running' ? 'on' : 'blocked';
    }

    function watchAudioState(context) {
        const handleStateChange = function() {
            // Sound was held when the offer arrived: play it the moment the
            // browser allows, then keep the regular rhythm from there.
            if (context.state === 'running' && active && active.cuePending) {
                sound();
                restartRepeat();
            }
            emitChange();
        };

        try {
            if (typeof context.addEventListener === 'function') {
                context.addEventListener('statechange', handleStateChange);
            } else {
                context.onstatechange = handleStateChange;
            }
        } catch (error) {
            // The state is read again whenever the page asks for it.
        }
    }

    function getAudioContext(create) {
        if (audioContext || !create) {
            return audioContext;
        }

        const AudioContextApi = getAudioApi();

        if (!AudioContextApi) {
            return null;
        }

        try {
            audioContext = new AudioContextApi();
            watchAudioState(audioContext);
        } catch (error) {
            audioContext = null;
        }

        return audioContext;
    }

    // Asks once; a browser that still refuses keeps the promise pending or
    // rejects, and the state stays 'blocked'. Never queues a cue: a held cue
    // is replayed at most once, from the statechange handler.
    function resumeAudio(context) {
        if (!context || context.state === 'running' || typeof context.resume !== 'function') {
            return;
        }
        try {
            Promise.resolve(context.resume()).then(emitChange, () => undefined);
        } catch (error) {
            // Still blocked; the page says so.
        }
    }

    function unlockAudio() {
        if (isChimeEnabled()) {
            resumeAudio(getAudioContext(true));
        }

        return getAudioState();
    }

    function noteInteraction(event) {
        // A page script's synthetic click is not the interpreter.
        if (event && event.isTrusted === false) {
            return;
        }

        const first = !interacted;

        interacted = true;
        // Only a page that asked for offer sound (the dashboard, Go
        // available, an open offer, a preview) holds or creates a context to
        // start. A click anywhere else — a client in the conference page
        // loads this module too — creates no audio at all.
        if (audioContext
            ? audioContext.state !== 'running'
            : Boolean(active) || (soundOnInteraction && isChimeEnabled())) {
            unlockAudio();
        }
        if (first) {
            emitChange();
        }
    }

    function stopCue() {
        cue.forEach(({ gain, oscillator }) => {
            try {
                oscillator.stop();
            } catch (error) {
                // Already finished.
            }
            [ oscillator, gain ].forEach(node => {
                try {
                    if (typeof node.disconnect === 'function') {
                        node.disconnect();
                    }
                } catch (error) {
                    // Already disconnected.
                }
            });
        });
        cue = [];
    }

    // One soft two-tone (660 Hz, then 880 Hz), about 0.4 s. Stops the previous
    // cue first, so cues never overlap. Returns whether it actually sounded:
    // a context the browser still holds plays nothing.
    function playCue() {
        if (!isChimeEnabled()) {
            return false;
        }

        const context = getAudioContext(true);

        if (!context) {
            return false;
        }
        if (context.state !== 'running') {
            resumeAudio(context);

            return false;
        }

        stopCue();
        try {
            const startAt = context.currentTime + 0.01;

            [ [ 660, 0 ], [ 880, 0.2 ] ].forEach(([ frequency, offset ]) => {
                const at = startAt + offset;
                const oscillator = context.createOscillator();
                const gain = context.createGain();

                oscillator.type = 'sine';
                oscillator.frequency.setValueAtTime(frequency, at);
                gain.gain.setValueAtTime(0.0001, at);
                gain.gain.exponentialRampToValueAtTime(0.07, at + 0.02);
                gain.gain.exponentialRampToValueAtTime(0.0001, at + 0.18);
                oscillator.connect(gain);
                gain.connect(context.destination);
                oscillator.start(at);
                oscillator.stop(at + 0.2);
                cue.push({ gain, oscillator });
            });
        } catch (error) {
            stopCue();

            return false;
        }

        return true;
    }

    // The current offer's cue. Remembers a held cue so it plays once the
    // browser allows sound, instead of stacking one per tick.
    function sound() {
        if (!active) {
            return;
        }
        active.cuePending = !playCue() && isChimeEnabled();
    }

    function clearChimeTimer() {
        if (chimeTimer !== null) {
            environment.clearInterval(chimeTimer);
            chimeTimer = null;
        }
    }

    function restartRepeat() {
        clearChimeTimer();
        if (active && active.repeat && typeof environment.setInterval === 'function') {
            chimeTimer = environment.setInterval(sound, CHIME_REPEAT_MS);
        }
    }

    // The settings preview and the page's "Turn on sound": one cue, after the
    // browser has started the sound this click unlocked.
    function previewChime() {
        if (!isChimeEnabled()) {
            return false;
        }

        const context = getAudioContext(true);

        if (!context) {
            return false;
        }
        if (context.state === 'running') {
            return playCue();
        }
        try {
            Promise.resolve(context.resume()).then(() => {
                if (context.state === 'running') {
                    playCue();
                }
            }, () => undefined);
        } catch (error) {
            return false;
        }

        return true;
    }

    function setChimeEnabled(enabled, preview) {
        if (!writePreference(CHIME_STORAGE_KEY, enabled)) {
            return false;
        }

        if (enabled) {
            if (preview) {
                previewChime();
            } else {
                unlockAudio();
            }
        } else {
            // Turned off during an offer: silent at once.
            stopCue();
            if (active) {
                active.cuePending = false;
            }
        }
        emitChange();

        return true;
    }

    // A page that sounds every offer the moment it arrives (the interpreter
    // dashboard) sets the audio up at load — the browser may hold it — so
    // the sound state it shows is the browser's real answer, and the first
    // click or key press anywhere on the page starts it.
    function enableSoundOnInteraction() {
        soundOnInteraction = true;

        return isChimeEnabled() ? unlockAudio() : getAudioState();
    }

    // Saved for the signed-in account only (see setAutoAnswerOwner); false
    // when no account is known, so nothing is saved for "whoever is next".
    function setAutoAnswerEnabled(enabled) {
        const key = autoAnswerStorageKey();
        const saved = key ? writePreference(key, enabled) : false;

        emitChange();

        return saved;
    }

    function setAutoAnswerOwner(owner) {
        autoAnswerOwner = owner === undefined ? null : owner;
        emitChange();
    }

    // ── What the offer says ────────────────────────────────────────────
    function offerLabel(request) {
        // A page that is not the call queue names its own alert (Jobs: "Job
        // offer" — on Jobs, a "request" is the interpreter asking).
        if (typeof request?.label === 'string' && request.label.trim()) {
            return request.label.trim();
        }
        if (request?.isTransfer) {
            return 'Call transfer request';
        }
        if ((request?.requestKind || request?.request_kind) === 'inbound_hearing') {
            return 'Inbound hearing call';
        }
        if (String(request?.callType || request?.call_type || '').toLowerCase() === 'vri') {
            return 'VRI interpreter request';
        }

        return 'Interpreter request';
    }

    function offerTitle(request) {
        const label = typeof request?.label === 'string' ? request.label.trim() : '';

        return label ? '! ' + label + ' - respond now' : CALL_OFFER_TITLE;
    }

    // Never the client's name: a notification can be read over a shoulder
    // or kept by the operating system.
    function offerBody(request, options) {
        const language = String(request?.language || '').trim();
        const lead = language ? language + ' request. ' : '';

        return options?.autoAnswer
            ? lead + 'Answering automatically. Open the interpreter portal to decline.'
            : lead + 'Open the interpreter portal to accept or decline.';
    }

    // ── Screen, tab title and system notification ───────────────────────
    function createBeacon() {
        const documentRef = getDocument();

        if (!documentRef?.body || typeof documentRef.createElement !== 'function') {
            return null;
        }

        const element = documentRef.createElement('div');

        element.id = 'malka-interpreter-offer-beacon';
        element.setAttribute('aria-hidden', 'true');
        element.style.cssText = [
            'position:fixed',
            'inset:0',
            'box-sizing:border-box',
            'border:8px solid #f59e0b',
            'box-shadow:inset 0 0 36px rgba(245,158,11,0.42)',
            'opacity:1',
            'pointer-events:none',
            'z-index:2147483646'
        ].join(';');
        documentRef.body.appendChild(element);

        return element;
    }

    // Hidden, or visible behind another window. A document that cannot say
    // counts as background: better one notification too many than a missed call.
    function isPageInBackground() {
        const documentRef = getDocument();

        if (!documentRef) {
            return true;
        }
        if (documentRef.hidden === true || documentRef.visibilityState === 'hidden') {
            return true;
        }
        if (typeof documentRef.hasFocus === 'function') {
            try {
                return !documentRef.hasFocus();
            } catch (error) {
                return true;
            }
        }

        return typeof documentRef.hidden !== 'boolean';
    }

    // The system notification is for an interpreter who is not looking at
    // this tab: raised when the offer arrives in the background, or when the
    // tab goes to the background while the offer is still open.
    function notifyIfBackground() {
        if (!active || notification || !isPageInBackground()) {
            return;
        }

        const NotificationApi = getNotificationApi();

        if (!NotificationApi || NotificationApi.permission !== 'granted') {
            return;
        }
        try {
            const createdNotification = new NotificationApi(active.label, {
                body: active.body,
                requireInteraction: true,
                tag: NOTIFICATION_TAG
            });

            // Clicked or dismissed: forget it, so a later trip to the
            // background during the same offer raises a fresh one.
            const forget = function() {
                if (notification === createdNotification) {
                    notification = null;
                    emitChange();
                }
            };

            notification = createdNotification;
            createdNotification.onclick = function() {
                try {
                    environment.focus();
                } catch (error) {
                    // The tab title and the visual beacon remain actionable.
                }
                try {
                    createdNotification.close();
                } catch (error) {
                    // Already closed.
                }
                forget();
            };
            createdNotification.onclose = forget;
        } catch (error) {
            notification = null;
        }
        emitChange();
    }

    // The page changed whether this offer will answer by itself (the tab
    // went out of view, the interpreter chose to answer, the tab came back).
    // A notification on screen is replaced so it says what will happen.
    function setAutoAnswering(enabled) {
        if (!active) {
            return;
        }

        const body = offerBody(active.offer, { autoAnswer: enabled === true });

        if (body === active.body) {
            return;
        }
        active.body = body;
        if (notification) {
            const previous = notification;

            notification = null;
            try {
                previous.close();
            } catch (error) {
                // Already closed.
            }
            notifyIfBackground();
        }
    }

    function stop() {
        const documentRef = getDocument();
        const wasActive = Boolean(active);

        active = null;
        clearChimeTimer();
        stopCue();
        if (titleTimer !== null) {
            environment.clearInterval(titleTimer);
            titleTimer = null;
        }
        if (documentRef && originalTitle !== null) {
            documentRef.title = originalTitle;
        }
        originalTitle = null;

        if (beacon) {
            beacon.remove();
            beacon = null;
        }
        if (notification) {
            try {
                notification.close();
            } catch (error) {
                // The notification may already have been closed by its click.
            }
            notification = null;
        }
        if (wasActive) {
            emitChange();
        }
    }

    // options.repeatChime: sound every CHIME_REPEAT_MS until stopped (a live
    // call offer). Without it one cue plays (Jobs, the in-meeting notice).
    // options.autoAnswer: the page will answer by itself; the notification
    // says so.
    function start(request, options) {
        stop();

        const offer = request || {};
        const settings = options || {};
        const documentRef = getDocument();
        const reduceMotion = Boolean(environment.matchMedia
            && environment.matchMedia('(prefers-reduced-motion: reduce)').matches);
        const activation = {
            body: offerBody(offer, settings),
            cuePending: false,
            label: offerLabel(offer),
            offer,
            repeat: settings.repeatChime === true,
            title: offerTitle(offer)
        };
        let showingOffer = true;

        active = activation;
        if (documentRef) {
            originalTitle = documentRef.title || '';
            documentRef.title = activation.title;
            beacon = createBeacon();

            if (typeof environment.setInterval === 'function') {
                titleTimer = environment.setInterval(function() {
                    showingOffer = !showingOffer;
                    documentRef.title = showingOffer ? activation.title : originalTitle;
                    if (beacon && !reduceMotion) {
                        beacon.style.opacity = showingOffer ? '1' : '0.28';
                    }
                }, FLASH_INTERVAL_MS);
            }
        }

        notifyIfBackground();
        sound();
        restartRepeat();
        emitChange();

        // Stops this offer's attention only, never a newer offer's.
        return function stopThisOffer() {
            if (active === activation) {
                stop();
            }
        };
    }

    // Go available (usually a click): start the sound now if the browser
    // allows it, and ask once for notifications.
    function prepare() {
        unlockAudio();

        return requestNotificationPermission();
    }

    function getStatus() {
        const permission = getNotificationPermission();

        return {
            active: Boolean(active),
            audio: getAudioState(),
            autoAnswerEnabled: isAutoAnswerEnabled(),
            background: isPageInBackground(),
            chimeEnabled: isChimeEnabled(),
            interacted: hasUserInteraction(),
            notificationPermission: permission,
            notificationShown: Boolean(notification),
            notificationSupported: permission !== 'unsupported'
        };
    }

    (function installListeners() {
        const documentRef = getDocument();

        if (documentRef && typeof documentRef.addEventListener === 'function') {
            INTERACTION_EVENTS.forEach(type => {
                documentRef.addEventListener(type, noteInteraction, { capture: true, passive: true });
            });
            documentRef.addEventListener('visibilitychange', notifyIfBackground);
        }
        if (environment && typeof environment.addEventListener === 'function') {
            environment.addEventListener('blur', notifyIfBackground);
        }
    }());

    return {
        enableSoundOnInteraction,
        getStatus,
        hasUserInteraction,
        onChange,
        prepare,
        previewChime,
        requestNotificationPermission,
        setAutoAnswerEnabled,
        setAutoAnswerOwner,
        setAutoAnswering,
        setChimeEnabled,
        start,
        stop,
        unlockAudio
    };
}));
